import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { all_routes } from "../../../router/all_routes";
import CollapseHeader from "../../../../core/common/collapse-header/collapse-header";
import Datatable from "../../../../core/common/dataTable";
import { ColumnsType } from "antd/es/table";
import { getSearchableHeader } from "../../../utils/getSearchableHeader";
import { cellWidth } from "../../../../core/methods/tableCellWidth";
import Footer from "../../../../core/common/Footer";
import useApi from "../../../../core/customhook/useApi";
import Swal from "sweetalert2";
import { displayValue } from "../../../../core/common/TabelValue";
import SnipperLoader from "../../../../core/common/loader/SnipperLoader";

const ViewAllPackage = () => {
  const navigate = useNavigate();
  const [packageList, setPackageList] = useState<any[]>([]);
  const [columnSearch, setColumnSearch] = useState<Record<string, string>>({});

  const getPackageList = useApi<any>("/api/v1/admin/all-packages", {
    method: "GET",
    autoCall: false,
    handleGlobalError: false,
  });

  const updatePackageStatus = useApi<any>("", {
    method: "PATCH",
    autoCall: false,
    handleGlobalError: false,
  });

  const packageListLoading = getPackageList.loading;

  useEffect(() => {
    const getPackageData = async () => {
      try {
        const res = await getPackageList.callApi();
        if (res?.success) {
          setPackageList(res?.data || []);
        }
      } catch (error) {
        console.error("Failed to fetch accounts:", error);
      }
    };

    getPackageData();
  }, []);

  // ---- Filtered Data ----
  const filteredData = useMemo(() => {
    return packageList.filter((row) =>
      Object.entries(columnSearch).every(([key, value]) =>
        value
          ? row[key as keyof typeof row]
              ?.toString()
              .toLowerCase()
              .includes(value.toLowerCase())
          : true,
      ),
    );
  }, [packageList, columnSearch]);

  const handleStatusToggle = async (record: any) => {
    // Save previous status
    const previousStatus = record.status;

    // Optimistically update UI
    setPackageList((prev: any[]) =>
      prev.map((item) =>
        item.package_id === record.package_id
          ? {
              ...item,
              status: !previousStatus,
            }
          : item,
      ),
    );

    try {
      const payload = {
        is_active: !previousStatus,
      };

      const res = await updatePackageStatus.callApi(
        payload,
        `/api/v1/admin/packages/${record.package_id}/status`,
      );

      if (!res?.success) {
        throw new Error(res?.message || "Unable to update package status.");
      }
    } catch (error: any) {
      // Revert UI if API fails
      setPackageList((prev: any[]) =>
        prev.map((item) =>
          item.package_id === record.package_id
            ? {
                ...item,
                status: previousStatus,
              }
            : item,
        ),
      );

      Swal.fire({
        icon: "error",
        title: "Update Failed",
        text:
          error?.response?.data?.message ||
          error?.message ||
          "Unable to update package status.",
      });
    }
  };

  // ---- Columns ----
  const columns: ColumnsType<any> = [
    {
      title: "Sr. No.",
      key: "srNo",
      width: 80,
      align: "center",
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      title: getSearchableHeader(
        "Package Name",
        "package_name",
        setColumnSearch,
      ),
      onCell: () => cellWidth(250),
      dataIndex: "package_name",
      key: "package_name",
      sorter: (a, b) => a.package_name.localeCompare(b.package_name),
      render: displayValue,
    },
    {
      title: getSearchableHeader("Code", "package_code", setColumnSearch),
      dataIndex: "package_code",
      key: "package_code",
      sorter: (a, b) => a.package_code.localeCompare(b.package_code),
      render: displayValue,
    },
    {
      title: getSearchableHeader("Checks", "sop", setColumnSearch),
      dataIndex: "sop",
      key: "sop",
      sorter: (a, b) => a.sop.localeCompare(b.sop),
      render: displayValue,
      onCell: () => cellWidth(400),
    },
    {
      title: "Active/Inactive",
      dataIndex: "status",
      key: "status",
      align: "center",
      render: (_: any, record: any) => (
        <div className="form-check form-switch d-flex justify-content-center">
          <input
            className="form-check-input"
            type="checkbox"
            checked={record.status}
            onChange={() => handleStatusToggle(record)}
          />
        </div>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (_: any, record: any) => (
        <button
          className="btn btn-sm btn-primary"
          onClick={() =>
            navigate(all_routes.editPackage, {
              state: {
                packageId: record.package_id,
              },
            })
          }
        >
          <i className="ti ti-edit"></i>
        </button>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Breadcrumb */}
        <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
          <div className="my-auto mb-2">
            <h2 className="mb-1">All Package</h2>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={all_routes.superAdminDashboard}>
                    <i className="ti ti-smart-home" />
                  </Link>
                </li>
                <li className="breadcrumb-item">Account Activation</li>
                <li className="breadcrumb-item">Account Configuration</li>
                <li className="breadcrumb-item">Manage Packages</li>
                <li className="breadcrumb-item active">All Package</li>
              </ol>
            </nav>
          </div>
          <div className="head-icons ms-2">
            <CollapseHeader />
          </div>
        </div>

        {/* Table  */}
        {packageListLoading ? (
          <SnipperLoader />
        ) : (
          <div className="card mt-3">
            <Datatable
              fixedHeader
              tableHeight={650}
              dataSource={filteredData}
              columns={columns}
              Selection={false}
            />
          </div>
        )}
        <div className="d-flex justify-content-end align-items-center my-4">
          <button
            className="btn btn-outline-primary px-4 d-flex align-items-center gap-2"
            onClick={() => navigate(-1)}
          >
            {/* <i className="ti ti-arrow-left"></i> */}
            <span>Back</span>
          </button>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ViewAllPackage;
